import React from 'react';
import CollectionPage from '../shared/CollectionPage';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { Navigate } from 'react-router-dom';

/**
 * Movie Collection Dashboard component
 * Uses the generic CollectionPage with movie-specific configuration
 */
const MovieDashboard = () => {
  const { isFeatureEnabled } = useFeatureFlags();
  
  // Check if the movies feature is enabled
  if (!isFeatureEnabled('enableMoviesFeature')) {
    return <Navigate to="/" replace />;
  }

  // Movie-specific filter options
  const movieFilterOptions = [
    // The default 'All Formats' option is added automatically by the FilterSelector component
    // so we only need to add the specific format options
    { value: 'DVD', label: 'DVD' },
    { value: 'Blu-ray', label: 'Blu-ray' },
    { value: '4K Ultra HD', label: '4K Ultra HD' },
    { value: 'VHS', label: 'VHS' },
    { value: 'LaserDisc', label: 'LaserDisc' },
    { value: 'Digital', label: 'Digital' },
    { value: 'Other', label: 'Other' }
  ];

  // Movie-specific render props
  const movieRenderProps = {
    getImageUrl: (item) => item.imageUrl,
    getItemName: (item) => item.name,
    getItemSubtitle: (item) => `Format: ${item.format || 'Not specified'} • Year: ${item.year || 'N/A'}`,
    getItemDetails: (item) => `Director: ${item.director || 'N/A'} • Genre: ${item.genre || 'N/A'}`,
    // No price functionality for movies
    showMarketValue: () => false,
    renderPriceValue: () => null
  };

  // Movie-specific filter function
  const getMovieFilterValue = (item) => item.format;

  // Movie-specific edit fields
  const movieEditFields = [
    {
      name: 'name',
      label: 'Title',
      type: 'text',
      required: true,
      gridProps: { xs: 12 }
    },
    {
      name: 'format',
      label: 'Format',
      type: 'select',
      options: [
        { value: 'DVD', label: 'DVD' },
        { value: 'Blu-ray', label: 'Blu-ray' },
        { value: '4K Ultra HD', label: '4K Ultra HD' },
        { value: 'VHS', label: 'VHS' },
        { value: 'LaserDisc', label: 'LaserDisc' },
        { value: 'Digital', label: 'Digital' },
        { value: 'Other', label: 'Other' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: [
        { value: 'Brand New (BNIB / NIB)', label: 'Brand New (BNIB / NIB)' },
        { value: 'New Old Stock (NOS)', label: 'New Old Stock (NOS)' },
        { value: 'Factory Sealed', label: 'Factory Sealed' },
        { value: 'Mint Condition (MINT)', label: 'Mint Condition (MINT)' },
        { value: 'Like New', label: 'Like New' },
        { value: 'Complete in Box (CIB)', label: 'Complete in Box (CIB)' },
        { value: 'Box Only / Case Only', label: 'Box Only / Case Only' },
        { value: 'Disc Only (Loose)', label: 'Disc Only (Loose)' },
        { value: 'Good / Acceptable', label: 'Good / Acceptable' },
        { value: 'Damaged Case / Cover', label: 'Damaged Case / Cover' },
        { value: 'For Parts / Not Working', label: 'For Parts / Not Working' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'edition',
      label: 'Edition',
      type: 'select',
      options: [
        { value: 'Not Specified', label: 'Not Specified' },
        { value: 'Standard Edition', label: 'Standard Edition' },
        { value: 'Special Edition', label: 'Special Edition' },
        { value: 'Director\'s Cut', label: 'Director\'s Cut' },
        { value: 'Extended Edition', label: 'Extended Edition' },
        { value: 'Collector\'s Edition', label: 'Collector\'s Edition' },
        { value: 'Limited Edition', label: 'Limited Edition' },
        { value: 'Anniversary Edition', label: 'Anniversary Edition' },
        { value: 'Criterion Collection', label: 'Criterion Collection' },
        { value: 'Steelbook', label: 'Steelbook' },
        { value: 'Box Set', label: 'Box Set' },
        { value: 'Ultimate Edition', label: 'Ultimate Edition' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'year',
      label: 'Year',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'director',
      label: 'Director',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'genre',
      label: 'Genre',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'runtime',
      label: 'Runtime',
      type: 'text',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'imageUrl',
      label: 'Image URL',
      type: 'text',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { value: 'Low', label: 'Low' },
        { value: 'Medium', label: 'Medium' },
        { value: 'High', label: 'High' }
      ],
      required: true,
      gridProps: { xs: 12, sm: 6 }
    },
    {
      name: 'watched',
      label: 'Watched',
      type: 'checkbox',
      required: false,
      gridProps: { xs: 12, sm: 6 }
    },

    {
      name: 'rating',
      label: 'Rating',
      type: 'rating',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      required: false,
      gridProps: { xs: 12 }
    },
    {
      name: 'plot',
      label: 'Plot',
      type: 'textarea',
      required: false,
      gridProps: { xs: 12 }
    }
  ];

  // Define table columns for sorting functionality
  const tableColumns = {
    name: { label: 'Title', sortable: true },
    format: { label: 'Format', sortable: true },
    year: { label: 'Year', sortable: true },
    director: { label: 'Director', sortable: true },
    genre: { label: 'Genre', sortable: true },
    condition: { label: 'Condition', sortable: true },
    watched: { label: 'Watched', sortable: true },

  };

  return (
    <CollectionPage
      collectionType="movies"
      title="Movie Collection"
      renderProps={movieRenderProps}
      filterOptions={movieFilterOptions}
      getFilterValue={getMovieFilterValue}
      editFields={movieEditFields}
      tableColumns={tableColumns}
      itemType="movie"
    />
  );
};

export default MovieDashboard;
